import React, { useState, useEffect, useCallback } from 'react';
import { getHomePageData } from '../services/homeService';
import { getStrapiMedia, renderSafeText, BASE_URL } from '../lib/strapi';

const Home = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const fetchHome = async () => {
      try {
        const result = await getHomePageData();
        if (!result) throw new Error('No content received. Ensure it is Published in Strapi.');
        setData(result);
      } catch (err) {
        console.error('Home page fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchHome();
  }, []);

  const testimonials = data?.testimonials_list || [];
  const nextTestimonial = useCallback(() => {
    setActiveTestimonial((prev) => (prev + 1) % (testimonials.length || 1));
  }, [testimonials.length]);

  useEffect(() => {
    if (testimonials.length <= 1) return;
    const interval = setInterval(nextTestimonial, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length, nextTestimonial]);

  if (loading) {
    return (
      <div className="py-40 text-center flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-widya-green border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">
          Loading Content...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 px-6">
        <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 p-8 rounded-2xl text-center">
          <h2 className="text-red-700 font-bold text-xl mb-2">Connection Error</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Target: {BASE_URL}/api/home-page</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const heroImageUrl = getStrapiMedia(data.hero_image);
  const aboutVideoUrl = getStrapiMedia(data.about_video);
  const missionVisionImageUrl = getStrapiMedia(data.missionvision_image);
  const statsImageUrl = getStrapiMedia(data.stats_image);

  const renderTeamRegion = (title, members) => {
    const validMembers = members || [];
    return (
      <div className="flex flex-col mb-8">
        <h3 className="text-[26px] md:text-[30px] font-bold text-[#67ba8e] text-center mb-6">{title}</h3>
        <div className="border border-[#67ba8e]/60 rounded-sm p-4 md:p-6 bg-white flex flex-wrap justify-center gap-x-2 gap-y-8 shadow-sm min-h-[100px]">
          {validMembers.length === 0 ? (
            <p className="text-gray-400 text-sm py-8 text-center italic w-full">Content will appear here once populated in Strapi.</p>
          ) : (
            validMembers.map((member, index) => {
              const photoUrl = getStrapiMedia(member.profile_image);
              return (
                <div key={member.id || index} className="flex flex-col items-center text-center w-[130px] sm:w-[150px]">
                  <div className="w-[110px] h-[130px] mb-3 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center shadow-sm">
                    {photoUrl ? (
                      <img src={photoUrl} alt={member.name} className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="text-gray-300 text-4xl font-bold">{member.name?.charAt(0)}</div>
                    )}
                  </div>
                  <h5 className="text-[12px] font-bold text-black mb-1 leading-tight">{member.name}</h5>
                  <p className="text-[10px] text-gray-600 leading-snug px-1 whitespace-pre-wrap">{member.role}</p>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <section
        id="hero"
        className="relative min-h-[600px] flex items-center justify-center text-white overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: heroImageUrl
              ? `url(${heroImageUrl})`
              : 'linear-gradient(135deg, #2D2D2D 0%, #1A1A1A 100%)',
          }}
        />
        <div className="absolute inset-0 bg-black/50" />

        <div className="relative z-10 mx-auto px-6 md:px-20 lg:px-[140px] xl:px-[180px] py-24 text-center max-w-[1100px]">
          <h1 className="text-[30px] md:text-[44px] lg:text-[68px] font-bold leading-[1.1] tracking-[-2px] mb-[18px] text-white font-heading">
            {renderSafeText(data.hero_title, 'Empowering Agriculture')}
          </h1>
          <h5 className="text-widya-gold font-normal text-[18px] md:text-[20px] leading-[1.5] tracking-[-0.5px] mt-6">
            {renderSafeText(data.hero_subtitle)}
          </h5>
        </div>
      </section>

      <section id="about" className="py-20 px-6 bg-white">
        <div className="mx-auto max-w-[1100px]">
          <div className="text-center mb-[40px]">
            <h2 className="text-[40px] font-bold leading-[48px] tracking-[-1px] text-black font-heading mb-[15px]">
              {renderSafeText(data.about_title, 'About WIDYA')}
            </h2>
            <span className="section-underline-center" />
          </div>

          <div className="grid lg:grid-cols-2 gap-[30px] items-start">
            <div className="rounded-2xl overflow-hidden shadow-lg bg-gray-100 aspect-video">
              {aboutVideoUrl ? (
                <video
                  controls
                  className="w-full h-full object-cover"
                  poster={heroImageUrl || ''}
                >
                  <source src={aboutVideoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400">
                  Video unavailable
                </div>
              )}
            </div>

            <div className="mt-8 lg:mt-0">
              {renderSafeText(data.about_description)
                .split('\n\n')
                .filter(Boolean)
                .map((paragraph, i) => (
                  <p
                    key={i}
                    className="text-[#333333] text-[18px] font-normal leading-[23px] tracking-[-0.5px] mt-[24px] mb-[15px]"
                  >
                    {paragraph}
                  </p>
                ))}
            </div>
          </div>
        </div>
      </section>

      <section id="mission-vision" className="py-20 px-6 bg-gray-50">
        <div className="mx-auto max-w-[1100px]">
          <div className="grid lg:grid-cols-2 gap-[30px] items-start">
            <div className="space-y-[40px] pr-[15px]">
              <div>
                <h3 className="text-[40px] font-bold leading-[48px] tracking-[-1px] text-black font-heading mb-[15px]">
                  {renderSafeText(data.mission_title, 'Our Mission')}
                </h3>
                <span className="section-underline" />
                <p className="text-[#333333] text-[18px] font-normal leading-[23px] tracking-[-0.5px] mt-[24px]">
                  {renderSafeText(data.mission_text)}
                </p>
              </div>

              <div>
                <h3 className="text-[40px] font-bold leading-[48px] tracking-[-1px] text-black font-heading mb-[15px]">
                  {renderSafeText(data.vision_title, 'Our Vision')}
                </h3>
                <span className="section-underline" />
                <p className="text-[#333333] text-[18px] font-normal leading-[23px] tracking-[-0.5px] mt-[24px]">
                  {renderSafeText(data.vision_text)}
                </p>
              </div>
            </div>

            <div className="relative">
              {missionVisionImageUrl ? (
                <img
                  src={missionVisionImageUrl}
                  alt="Farmers"
                  className="rounded-2xl shadow-xl w-full h-auto object-cover"
                />
              ) : (
                <div className="bg-gray-200 rounded-2xl h-96 flex items-center justify-center text-gray-400">
                  Image
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section id="benefits" className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-[1100px]">
          <div className="text-center mb-16">
            <h2 className="text-[40px] font-bold leading-[48px] tracking-[-1px] text-black font-heading mb-[15px]">
              {renderSafeText(data.benefits_section_title, 'A technology platform that helps farmers')}
            </h2>
            <span className="section-underline-center" />
          </div>

          <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
            {data.benefits_list?.map((benefit, index) => (
              <div
                key={benefit.id || index}
                className="bg-transparent"
              >
                <h4 className="text-[24px] font-bold text-[#67ba8e] mb-4 font-heading tracking-tight">
                  {renderSafeText(benefit.title)}
                </h4>
                <p className="text-[#333333] text-[18px] font-normal leading-[23px] tracking-[-0.5px]">
                  {renderSafeText(benefit.description)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="sdg" className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-[1100px]">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-[40px] font-bold leading-[48px] tracking-[-1px] text-black font-heading mb-[15px]">
                {renderSafeText(data.sdg_section_title, 'Alignment with UN Sustainable Development Goals (SDGs)')}
              </h2>
              <div className="w-16 h-[3px] bg-widya-green mb-8" />
              
              <div className="space-y-6">
                {data.sdg_list?.map((sdg, index) => {
                  let textStr = renderSafeText(sdg.description);
                  if (typeof textStr === 'string') {
                    textStr = textStr.replace(/(SDG \d+( \([^)]+\))?)/, '<strong>$1</strong>');
                  }
                  return (
                    <div key={sdg.id || index} className="flex items-start gap-4 border-l-2 border-widya-green pl-4">
                      <p 
                        className="text-widya-body text-[16px] leading-relaxed" 
                        dangerouslySetInnerHTML={{ __html: textStr }} 
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative pt-12">
              <div className="grid grid-cols-3 gap-2 md:gap-4 max-w-lg ml-auto">
                <div className="col-start-3">
                  {data.sdg_image?.[5] && <img src={getStrapiMedia(data.sdg_image[5])} alt="SDG 17" className="w-full h-auto shadow-md" />}
                </div>
                
                <div className="col-start-2">
                  {data.sdg_image?.[0] && <img src={getStrapiMedia(data.sdg_image[0])} alt="SDG 13" className="w-full h-auto shadow-md" />}
                </div>
                <div className="col-start-3">
                  {data.sdg_image?.[3] && <img src={getStrapiMedia(data.sdg_image[3])} alt="SDG 9" className="w-full h-auto shadow-md" />}
                </div>

                <div className="col-start-1">
                  {data.sdg_image?.[1] && <img src={getStrapiMedia(data.sdg_image[1])} alt="SDG 1" className="w-full h-auto shadow-md" />}
                </div>
                <div className="col-start-2">
                  {data.sdg_image?.[2] && <img src={getStrapiMedia(data.sdg_image[2])} alt="SDG 2" className="w-full h-auto shadow-md" />}
                </div>
                <div className="col-start-3">
                  {data.sdg_image?.[4] && <img src={getStrapiMedia(data.sdg_image[4])} alt="SDG 3" className="w-full h-auto shadow-md" />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="research" className="py-24 px-6 bg-white overflow-hidden">
        <div className="container mx-auto max-w-[1100px]">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            <div className="lg:col-span-5 relative pr-8 pb-8 flex flex-col items-center lg:items-start justify-center">
              
              <div className="relative z-10 w-full max-w-[420px] aspect-[4/5]">
                <div className="absolute -bottom-6 -right-6 w-full h-full border-2 border-[#67ba8e] rounded-[2rem] rounded-tl-none rounded-br-none -z-10" />

                {statsImageUrl ? (
                  <img
                    src={statsImageUrl}
                    alt="Research"
                    className="relative rounded-[2rem] shadow-2xl w-full h-full object-cover"
                  />
                ) : (
                  <div className="relative bg-gray-200 rounded-[2rem] shadow-2xl w-full h-full flex items-center justify-center text-gray-400">
                    Image
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-7 pt-8">
              <h2 className="text-[40px] font-bold leading-[48px] tracking-[-1px] text-black font-heading mb-[15px]">
                {renderSafeText(data.stats_title, 'Research and New Knowledge')}
              </h2>
              <div className="w-16 h-[2px] bg-widya-green mb-8" />
              <p className="text-[#333333] text-[18px] font-normal leading-[23px] tracking-[-0.5px] mt-[24px] mb-12 pr-4">
                {renderSafeText(data.stats_description)}
              </p>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-5">
                {data.stats_list?.map((stat, index) => (
                  <div
                    key={stat.id || index}
                    className="bg-white rounded-xl shadow-[0_15px_40px_-15px_rgba(0,0,0,0.15)] flex flex-col items-center justify-center text-center p-3 sm:p-5 min-h-[140px] hover:-translate-y-1 transition-transform"
                  >
                    <p className="text-[26px] sm:text-[28px] lg:text-[30px] xl:text-[34px] font-black text-widya-green font-heading mb-2 leading-[1.1] tracking-tight">
                      {renderSafeText(stat.value)}
                    </p>
                    <p className="text-[10px] xl:text-[11px] font-bold uppercase tracking-widest text-[#aaaaaa] mt-1">
                      {renderSafeText(stat.label)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="team" className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-[1300px]">
          <div className="text-center mb-16">
            <h2 className="text-[40px] font-bold leading-[48px] tracking-[-1px] text-black font-heading mb-[15px]">
              {renderSafeText(data.team_title, 'Our Team')}
            </h2>
            <p className="text-[#333333] text-[18px] font-normal leading-[23px] tracking-[-0.5px] mt-[40px] max-w-4xl mx-auto text-center">
              {renderSafeText(data.team_subtitle, 'Our WIDYA team is a highly motivated and dedicated group of individuals from Australia, Sri Lanka, and India.')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start max-w-6xl mx-auto">
            <div className="flex flex-col gap-2">
              {renderTeamRegion('WIDYA Australia', data.team_australia)}
              {renderTeamRegion('WIDYA India', data.team_india)}
            </div>

            <div className="flex flex-col gap-2">
              {renderTeamRegion('WIDYA Sri Lanka', data.team_sri_lanka)}
            </div>
          </div>
        </div>
      </section>

      {data.brandlogos_images && data.brandlogos_images.length > 0 && (
        <section id="brandlogos" className="py-24 px-6 bg-white border-t border-gray-50">
          <div className="container mx-auto max-w-[1100px]">
            <div className="text-center mb-16">
              <h2 className="text-[40px] font-bold leading-[48px] tracking-[-1px] text-black font-heading mb-[15px]">
                {renderSafeText(data.brandlogos_title, 'Building partnerships and innovation worldwide')}
              </h2>
              <div className="w-16 h-[2px] bg-widya-green mx-auto mt-4" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-12 items-center justify-items-center opacity-80 hover:opacity-100 transition-opacity">
              {data.brandlogos_images.map((logo, index) => {
                const logoUrl = getStrapiMedia(logo);
                return logoUrl ? (
                  <img
                    key={logo.id || index}
                    src={logoUrl}
                    alt={`Partner ${index + 1}`}
                    className="max-w-[160px] max-h-[80px] w-auto h-auto object-contain transition-all duration-300"
                  />
                ) : null;
              })}
            </div>
          </div>
        </section>
      )}

      {testimonials.length > 0 && (
        <section id="testimonials" className="py-20 px-6 bg-widya-testimonial">
          <div className="container mx-auto max-w-4xl text-center text-white">
            <span className="block w-16 h-1 bg-white/40 mx-auto mb-12" />

            <div className="min-h-[180px] flex flex-col items-center justify-center">
              <div key={activeTestimonial} className="animate-slide-in">
                <svg className="w-10 h-10 text-white/30 mx-auto mb-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <blockquote className="text-xl md:text-2xl font-medium leading-relaxed mb-6 italic">
                  {renderSafeText(testimonials[activeTestimonial]?.quote)}
                </blockquote>
                {testimonials[activeTestimonial]?.author_role && (
                  <p className="text-white/70 text-sm font-semibold uppercase tracking-wider">
                    — {renderSafeText(testimonials[activeTestimonial]?.author_role)}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === activeTestimonial
                      ? 'bg-white scale-125'
                      : 'bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`Testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;